import { useCallback, useEffect, useState } from "react";
import "./CardInfoContent.scss";
import { ResponseInfo } from "./Response";
import { HeaderInfoContent } from "./Headers";
import { PathInfoContent } from "./Path";
import { QueryInfo } from "./Query";
import { ApiInfo } from "../api/ApiInfo";
import { MOCK_DATA } from "../../_mockData/MockData";
import { PROD_MOCK_DATA } from "../../_mockData/MockDataProd";
import { PermanentDrawerRight } from "../drawer/DrawerRight";
import { useLocation } from "react-router-dom";
import { BodyInfoContent } from "./Body";
import { BodyCodeView } from "./BodyCodeView";
import { useSelector } from "react-redux";

interface CardInfoProps {
  id: string;
}

// Replace path placeholders in a URL with the user-supplied values.
// Supports two styles seen in the mock data:
//   1. Braced placeholders, e.g. "/Patient/{PatientId}/$everything"
//   2. Bare label as a trailing path segment, e.g. ".../HealthcareService/id"
const substitutePathParams = (
  url: string,
  values: { [label: string]: string }
): string => {
  if (!url) return url;
  let result = url;
  Object.entries(values).forEach(([label, value]) => {
    if (!value || !label) return;
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const braced = new RegExp(`\\{${escaped}\\}`, "g");
    if (braced.test(result)) {
      result = result.replace(braced, value);
      return;
    }
    const segment = new RegExp(`/${escaped}(?=$|[/?#])`);
    result = result.replace(segment, `/${value}`);
  });
  return result;
};

export const CardInfoContent = (props: CardInfoProps) => {
  const location = useLocation();
  const environment = useSelector(
    (state: any) => state.registerApp.environment
  );
  const isProduction = environment === "production" ? true : false;
  const isTokenRoute = location.pathname.includes("/Authorization/Token");
  const isRefreshTokenRoute = location.pathname.includes(
    "/Authorization/RefreshToken"
  );
  const activeContent = isProduction
    ? PROD_MOCK_DATA[props.id]
    : MOCK_DATA[props.id];
  const activeRoute = props.id ?? "";
  const [query, setQuery] = useState({});
  const [pathValues, setPathValues] = useState<{ [label: string]: string }>({});
  const [rawHeaders, setRawHeaders] = useState({});

  const handleQuery = (data: any) => {
    let strQuery = ``;
    Object.keys(data).forEach((each: string, index: number) => {
      if (data[each]) {
        strQuery += index === 0 ? "?" : "&";
        strQuery += `${each}=${data[each]}`;
      }
    });
    if (activeRoute) {
      let result: any = {};
      result[activeRoute] = strQuery ?? "";
      setQuery(result);
    }
  };

  const handlePath = useCallback((data: { [label: string]: string }) => {
    setPathValues(data);
  }, []);

  const handleHeaders = (data: any) => {
    setRawHeaders(data);
  };

  const handleBody = (data: any) => {
    if (activeRoute) {
      let result: any = {};
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          result[key] = data[key];
        }
      });
    }
  };

  // Reset transient input state whenever the user navigates between
  // endpoints. Prevents stale path/query values from leaking into a
  // newly selected route's request URL.
  useEffect(() => {
    setPathValues({});
    setQuery({});
    setRawHeaders({});
  }, [props.id]);

  const resolvedCodeUrl =
    activeContent.path?.length > 0
      ? substitutePathParams(activeContent.codeurl, pathValues)
      : activeContent.codeurl;
  const queryString =
    activeContent.queryParams?.length > 0
      ? (query as any)?.[activeRoute] ?? ""
      : "";
  const codeUrl = `${resolvedCodeUrl}${queryString}`;

  return (
    <section className="page-layout">
      <ApiInfo url={activeContent.url} languageurl={activeContent.languageurl} />
      {activeContent.path?.length > 0 && (
        <PathInfoContent
          pathinfo={activeContent.path}
          handlePath={handlePath}
        />
      )}
      {activeContent.queryParams?.length > 0 && (
        <QueryInfo
          queryParams={activeContent.queryParams}
          handleQuery={handleQuery}
          activeRoute={activeRoute}
        />
      )}
      <HeaderInfoContent
        handleHeaders={handleHeaders}
        headerInfo={activeContent.header}
        activeRoute={activeRoute}
      />
      {activeContent.bodyCode && (
        <BodyCodeView code={activeContent.bodyCode} />
      )}
      {(isTokenRoute || isRefreshTokenRoute) && (
        <BodyInfoContent
          bodyInfo={activeContent.info}
          handleBody={handleBody}
        />
      )}
      <ResponseInfo responses={activeContent.response} />
      <PermanentDrawerRight
        activeRoute={activeRoute}
        rawHeaders={rawHeaders}
        languageurl={activeContent.languageurl}
        codeurl={codeUrl}
      />
    </section>
  );
};
